/**
 * Dynamic Ring Size Selector
 * 
 * This script handles dynamic ring size selection based on metafield values.
 * It creates virtual variants for ring sizes that don't exist in the product variants.
 */

class DynamicRingSizeHandler {
  constructor() {
    this.ringSizeData = window.dynamicRingSizes;
    this.variantPicker = null;
    this.originalVariants = null;
    this.init();
  }

  init() {
    if (!this.ringSizeData) return;

    // Wait for variant picker to be ready
    this.waitForVariantPicker().then(() => {
      this.setupDynamicRingSize();
    });
  }

  waitForVariantPicker() {
    return new Promise((resolve) => {
      const checkVariantPicker = () => {
        this.variantPicker = document.querySelector('variant-picker');
        if (this.variantPicker && this.variantPicker.hasAttribute('loaded')) {
          resolve();
        } else {
          setTimeout(checkVariantPicker, 100);
        }
      };
      checkVariantPicker();
    });
  }

  setupDynamicRingSize() {
    if (!this.variantPicker || !this.ringSizeData) return;

    // Store original variants data
    this.originalVariants = { ...this.variantPicker.data };

    // Find the ring size option selector
    const ringSizeSelector = this.findRingSizeSelector();
    if (!ringSizeSelector) return;

    // Create virtual variants for dynamic ring sizes
    this.createVirtualVariants();

    // Update the variant picker data
    this.updateVariantPickerData();

    // Handle dynamic size selection
    this.handleDynamicSizeSelection(ringSizeSelector);
  }

  findRingSizeSelector() {
    const selectors = this.variantPicker.querySelectorAll('.option-selector');
    for (const selector of selectors) {
      const optionName = selector.dataset.option;
      if (optionName && this.isRingSizeOption(optionName)) {
        return selector;
      }
    }
    return null;
  }

  isRingSizeOption(optionName) {
    const ringSizeKeywords = ['size', 'ring size', 'ring', 'sz'];
    const optionNameLower = optionName.toLowerCase();
    return ringSizeKeywords.some(keyword => optionNameLower.includes(keyword));
  }

  createVirtualVariants() {
    const virtualVariants = [];
    const baseVariant = this.findBaseVariant();

    if (!baseVariant) return;

    // Create variants for each dynamic ring size
    this.ringSizeData.sizes.forEach((size, index) => {
      const virtualVariant = {
        ...baseVariant,
        id: `virtual-${baseVariant.id}-${index}`,
        title: this.createVariantTitle(baseVariant, size),
        options: this.updateVariantOptions(baseVariant, size),
        available: true,
        price: baseVariant.price,
        compare_at_price: baseVariant.compare_at_price,
        sku: this.createVariantSku(baseVariant, size),
        barcode: this.createVariantBarcode(baseVariant, size),
        inventory_management: baseVariant.inventory_management,
        inventory_quantity: baseVariant.inventory_quantity,
        inventory_policy: baseVariant.inventory_policy,
        fulfillment_service: baseVariant.fulfillment_service,
        requires_shipping: baseVariant.requires_shipping,
        taxable: baseVariant.taxable,
        weight: baseVariant.weight,
        weight_unit: baseVariant.weight_unit,
        isVirtual: true,
        dynamicSize: size
      };

      virtualVariant.options[this.getRingSizeOptionIndex()] = size.toString();
      virtualVariants.push(virtualVariant);
    });

    this.virtualVariants = virtualVariants;
  }

  findBaseVariant() {
    // Find a variant that matches the base ring size or use the first available variant
    const baseSize = this.ringSizeData.baseSize.toString();
    const optionIndex = this.getRingSizeOptionIndex();

    for (const variant of this.originalVariants.variants) {
      if (variant.options[optionIndex] === baseSize) {
        return variant;
      }
    }

    // Fallback to first available variant
    return this.originalVariants.variants.find(v => v.available) || this.originalVariants.variants[0];
  }

  getRingSizeOptionIndex() {
    const selectors = this.variantPicker.querySelectorAll('.option-selector');
    for (let i = 0; i < selectors.length; i++) {
      const optionName = selectors[i].dataset.option;
      if (optionName && this.isRingSizeOption(optionName)) {
        return i;
      }
    }
    return 0;
  }

  createVariantTitle(baseVariant, size) {
    const titleParts = baseVariant.title.split(' / ');
    const optionIndex = this.getRingSizeOptionIndex();
    titleParts[optionIndex] = size.toString();
    return titleParts.join(' / ');
  }

  updateVariantOptions(baseVariant, size) {
    const options = [...baseVariant.options];
    const optionIndex = this.getRingSizeOptionIndex();
    options[optionIndex] = size.toString();
    return options;
  }

  createVariantSku(baseVariant, size) {
    if (!baseVariant.sku) return '';
    return `${baseVariant.sku}-${size}`;
  }

  createVariantBarcode(baseVariant, size) {
    if (!baseVariant.barcode) return '';
    return `${baseVariant.barcode}-${size}`;
  }

  updateVariantPickerData() {
    // Combine original variants with virtual variants
    const allVariants = [...this.originalVariants.variants, ...this.virtualVariants];
    
    // Update the variant picker's data
    this.variantPicker.data.variants = allVariants;

    // Update formatted data for virtual variants
    this.virtualVariants.forEach(variant => {
      this.variantPicker.data.formatted[variant.id] = {
        price: this.formatPrice(variant.price),
        compareAtPrice: variant.compare_at_price ? this.formatPrice(variant.compare_at_price) : null,
        unitPrice: variant.unit_price ? this.formatPrice(variant.unit_price) : null,
        inventory: variant.inventory_quantity <= 0 ? 'none' : null
      };
    });
  }

  formatPrice(price) {
    // Use the same formatting as the original variant picker
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return formatter.format(price / 100);
  }

  handleDynamicSizeSelection(ringSizeSelector) {
    // Add event listener for dynamic size changes
    ringSizeSelector.addEventListener('change', (event) => {
      if (event.target.hasAttribute('data-dynamic-size')) {
        this.handleVirtualVariantSelection(event.target.value);
      }
    });
  }

  handleVirtualVariantSelection(selectedSize) {
    // Find the virtual variant for the selected size
    const virtualVariant = this.virtualVariants.find(v => v.dynamicSize.toString() === selectedSize);
    
    if (virtualVariant) {
      // Update the variant picker's selected variant
      this.variantPicker.variant = virtualVariant;
      this.variantPicker.selectedOptions = virtualVariant.options;
      
      // Trigger variant change updates
      this.variantPicker.updateVariantInput();
      this.variantPicker.updateAddToCartButton();
      this.variantPicker.updateAvailability();
      this.variantPicker.updatePrice();
      this.variantPicker.updateSku();
      this.variantPicker.updateBarcode();
      
      // Dispatch variant change event
      this.variantPicker.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.variantPicker.productForm,
          variant: virtualVariant,
          allVariants: this.variantPicker.data.variants,
          selectedOptions: virtualVariant.options
        }
      }));
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (window.dynamicRingSizes) {
    new DynamicRingSizeHandler();
  }
});

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.dynamicRingSizes) {
      new DynamicRingSizeHandler();
    }
  });
} else {
  if (window.dynamicRingSizes) {
    new DynamicRingSizeHandler();
  }
}
